import React from 'react';
import { tokens } from '../../../theme';
import { Box, Typography, useTheme, CircularProgress, Divider, TextField, IconButton, Autocomplete } from "@mui/material";
import Header from '../../../components/Header';
import Axios from 'axios';
import Loading from '../../../components/Loading';
import Alert from '../../../components/alert/Alert';
import { useSignOut } from "react-auth-kit";
import { useNavigate } from "react-router-dom";
import { Document, Page, pdfjs } from "react-pdf";
import './Viewer.css';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import getCookies from '../../../utils/getCookies';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import Button from '@mui/material/Button';
import { Link } from 'react-router-dom';
import StudentAnswerConfirmModal from './StudentAnswerConfirmModal';
import { getAuthHeader } from '../../../utils/authToken';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const TestSubmitPage = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const currentUser = getCookies("_auth_state");
    const userRole = currentUser ? currentUser.split(',')[2]?.split(':')[1]?.split('"')[1] : null;
    const testId = window.location.pathname.split("/")[window.location.pathname.split("/").length - 2];
    const courseId = window.location.pathname.split("/")[2];
    const ownUuid = currentUser.split(',')[0]?.split(':')[1]?.split('"')[1];

    // Admin proxy mode: admin can act on behalf of a student
    const isAdminProxy = userRole === "admin";
    const urlParams = new URLSearchParams(window.location.search);
    const [proxyStudentUuid, setProxyStudentUuid] = React.useState(urlParams.get('student') || '');
    const [courseStudents, setCourseStudents] = React.useState([]);
    const studentUuid = isAdminProxy ? proxyStudentUuid : ownUuid;
    const canEdit = userRole === "student" || (isAdminProxy && proxyStudentUuid);

    // for 401 error
    const signOut = useSignOut();
    const navigate = useNavigate();
    const handleLogout = () => {
        signOut();
        localStorage.removeItem("userName");
        localStorage.removeItem("who");
        navigate("/signin");
    }
    const [loading, setLoading] = React.useState(true);

    const [pdfLoading, setPdfLoading] = React.useState(true);
    const [test, setTest] = React.useState(null);
    const [numPages, setNumPages] = React.useState(null);
    const [sections, setSections] = React.useState([]);

    const [testTakerInfo, setTestTakerInfo] = React.useState(null);

    // const [unansweredQuestions, setUnansweredQuestions] = React.useState([]);

    const [questionsForNoSectionTest, setQuestionsForNoSectionTest] = React.useState([]);

    // for alert
    const [successAlertOpen, setSuccessAlertOpen] = React.useState(false);
    const [errorAlertOpen, setErrorAlertOpen] = React.useState(false);
    const [successAlertMessage, setSuccessAlertMessage] = React.useState("");
    const [errorAlertMessage, setErrorAlertMessage] = React.useState("");


    const [dividerPosition, setDividerPosition] = React.useState(7); // Initial flex ratio
    const rightPaneRef = React.useRef(null);

    const handleMouseDown = (e) => {
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    const handleMouseMove = (e) => {
        if (rightPaneRef.current) {
            const newDividerPosition = (e.clientX / window.innerWidth) * 10;
            setDividerPosition(newDividerPosition);
        }
    };

    const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
    };

    const parseStudentAnswers = (studentAnswers, hasSection) => {
        if (hasSection) {
            return studentAnswers.split('@').map(section => section.split('|'));
        } else {
            return studentAnswers.split('|');
        }
    };
    
    // let parsedStudentAnswers = [];
    // if (testTakerInfo && testTakerInfo.student_answers && test) {
    //     parsedStudentAnswers = parseStudentAnswers(testTakerInfo.student_answers, test.has_section);
    // }

    // FRQ answers: { [questionId]: { [partLabel]: "text" } }
    const [frqAnswers, setFrqAnswers] = React.useState({});

    // FRQ upload images: { [questionId]: { [partLabel]: ["url1", "url2"] } }
    const [frqUploadImages, setFrqUploadImages] = React.useState({});
    const [uploadingParts, setUploadingParts] = React.useState({}); // { "qId_partLabel": true }

    // Choice group selections: { [choiceGroup]: questionId }
    const [choiceGroupSelections, setChoiceGroupSelections] = React.useState({});

    const isFrqQuestion = (question) => question.question_type === 'frq';
    const isSaQuestion = (question) => question.question_type === 'sa';

    const handleFrqAnswerChange = (questionId, partLabel, text) => {
        setFrqAnswers(prev => ({
            ...prev,
            [questionId]: {
                ...(prev[questionId] || {}),
                [partLabel]: text,
            }
        }));
    };

    const handleChoiceGroupChange = (choiceGroup, questionId) => {
        setChoiceGroupSelections(prev => ({
            ...prev,
            [choiceGroup]: questionId,
        }));
    };

    const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/heic', 'image/webp'];
    const MAX_IMAGES_PER_PART = 5;
    const MAX_FILE_SIZE_MB = 30;
    const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

    const handleImageUpload = async (questionId, partLabel, files) => {
        const uploadKey = `${questionId}_${partLabel}`;
        const currentImages = (frqUploadImages[questionId] && frqUploadImages[questionId][partLabel]) || [];

        // Filter valid image files, check size, and respect limit
        const allFiles = Array.from(files);
        const rejectedTypes = allFiles.filter(f => !ALLOWED_IMAGE_TYPES.includes(f.type));
        const rejectedSize = allFiles.filter(f => ALLOWED_IMAGE_TYPES.includes(f.type) && f.size > MAX_FILE_SIZE_BYTES);
        const validFiles = allFiles.filter(f => ALLOWED_IMAGE_TYPES.includes(f.type) && f.size <= MAX_FILE_SIZE_BYTES);

        if (rejectedTypes.length > 0) {
            setErrorAlertMessage(`Unsupported file type. Please upload JPG, PNG, HEIC, or WebP files only.`);
            setErrorAlertOpen(true);
        }
        if (rejectedSize.length > 0) {
            setErrorAlertMessage(`${rejectedSize.map(f => `"${f.name}"`).join(', ')} exceeds the ${MAX_FILE_SIZE_MB}MB limit.`);
            setErrorAlertOpen(true);
        }

        const slotsLeft = MAX_IMAGES_PER_PART - currentImages.length;
        if (validFiles.length > 0 && slotsLeft <= 0) {
            setErrorAlertMessage(`Maximum ${MAX_IMAGES_PER_PART} images per part. Remove an image to upload more.`);
            setErrorAlertOpen(true);
            return;
        }
        const filesToUpload = validFiles.slice(0, slotsLeft);

        if (filesToUpload.length === 0) return;

        setUploadingParts(prev => ({ ...prev, [uploadKey]: true }));

        try {
            // Request presigned URLs
            const presignedRes = await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/tests/${testId}/frq-upload-urls`,
                {
                    images: filesToUpload.map(f => ({
                        question_id: questionId,
                        part_label: partLabel,
                        file_name: f.name,
                        content_type: f.type,
                    })),
                },
                { headers: { Authorization: getAuthHeader() } }
            );

            const { urls } = presignedRes.data;
            const uploadedUrls = [];

            // Upload each file directly to S3
            for (let i = 0; i < filesToUpload.length; i++) {
                await Axios.put(urls[i].presignedUrl, filesToUpload[i], {
                    headers: { 'Content-Type': filesToUpload[i].type },
                });
                uploadedUrls.push(urls[i].publicUrl);
            }

            // Update state
            setFrqUploadImages(prev => ({
                ...prev,
                [questionId]: {
                    ...(prev[questionId] || {}),
                    [partLabel]: [...currentImages, ...uploadedUrls],
                },
            }));
        } catch (error) {
            console.error('Image upload failed:', error);
            setErrorAlertMessage('Failed to upload image(s). Please try again.');
            setErrorAlertOpen(true);
        } finally {
            setUploadingParts(prev => ({ ...prev, [uploadKey]: false }));
        }
    };

    const handleImageRemove = (questionId, partLabel, index) => {
        setFrqUploadImages(prev => {
            const current = (prev[questionId] && prev[questionId][partLabel]) || [];
            const updated = current.filter((_, i) => i !== index);
            return {
                ...prev,
                [questionId]: {
                    ...(prev[questionId] || {}),
                    [partLabel]: updated,
                },
            };
        });
    };

    const buildFrqAnswersPayload = () => {
        const result = [];
        // Collect all question+part combos from both text and images
        const allKeys = new Set();
        Object.entries(frqAnswers).forEach(([qId, parts]) => {
            Object.keys(parts).forEach(pl => allKeys.add(`${qId}__${pl}`));
        });
        Object.entries(frqUploadImages).forEach(([qId, parts]) => {
            Object.keys(parts).forEach(pl => allKeys.add(`${qId}__${pl}`));
        });

        allKeys.forEach(key => {
            const [questionId, partLabel] = key.split('__');
            const responseText = frqAnswers[questionId]?.[partLabel] || '';
            const responseImages = frqUploadImages[questionId]?.[partLabel] || [];
            const hasText = responseText && responseText.trim();
            const hasImages = responseImages.length > 0;
            if (hasText || hasImages) {
                result.push({
                    question_id: parseInt(questionId, 10),
                    part_label: partLabel,
                    response_text: hasText ? responseText : '',
                    ...(hasImages && { response_images: responseImages }),
                });
            }
        });
        return result;
    };

    // for modal
    const [modalOpen, setModalOpen] = React.useState(false);

    const handleModalOpen = () => {
        setModalOpen(true);
    };

    const handleModalClose = () => {
        setModalOpen(false);
    };

    // Fetch enrolled students for admin proxy mode
    React.useEffect(() => {
        if (!isAdminProxy) return;
        Axios.get(`${process.env.REACT_APP_URL}/api/v1/courses/${courseId}/students`, {
            headers: { Authorization: getAuthHeader() },
        }).then(res => {
            if (res.data) {
                // API returns student_courses with nested student object
                const students = res.data.map(sc => sc.student).filter(Boolean);
                setCourseStudents(students);
            }
        }).catch(() => {});
    }, [isAdminProxy, courseId]);

    React.useEffect(() => {
        localStorage.setItem('selectedMenu', 'Tests');
    
        async function fetchTest() {
            await Axios.get(`${process.env.REACT_APP_URL}/api/v1/courses/${courseId}/test-taking/${testId}`, {
                headers: {
                    Authorization: getAuthHeader(),
                },
            }).then(async (response) => {
                setTest(response.data);
                if (response.data.test_sections) {
                    setSections(response.data.test_sections.map(section => ({
                        ...section,
                        test_questions: section.test_questions.map(question => ({
                            ...question,
                            student_pick: ''
                        }))
                    })));
                } else {
                    setQuestionsForNoSectionTest(response.data.test_questions.map(question => ({
                        ...question,
                        student_pick: ''
                    })));
                }
            }).catch((error) => {
                if (error.response?.status === 404) {
                    setLoading(false);
                    setErrorAlertMessage('Test not found!');
                    setErrorAlertOpen(true);
                    return;
                } else if (error.response?.status === 401) {
                    handleLogout();
                    return;
                }
                setErrorAlertMessage(error.response?.data?.message || "Something went wrong");
                setErrorAlertOpen(true);
            });
        }
        fetchTest();
        setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    React.useEffect(() => {
        const fetchStudentTestInfo = async (studentUuid) => {
            try {
                const response = await Axios.get(`${process.env.REACT_APP_URL}/api/v1/courses/${courseId}/test-taking/${testId}/students/${studentUuid}/student-answers`, {
                    headers: {
                        Authorization: getAuthHeader(),
                    },
                });
    
                if (response && response.data) {
                    setTestTakerInfo(response.data);
                    if (response.data.student_answers) {
                        const parsedAnswers = parseStudentAnswers(response.data.student_answers, test.has_section);
                        if (test.has_section) {
                            setSections(prevSections => prevSections.map((section, sIndex) => ({
                                ...section,
                                test_questions: section.test_questions.map((question, qIndex) => ({
                                    ...question,
                                    student_pick: parsedAnswers[sIndex] && parsedAnswers[sIndex][qIndex] ? parsedAnswers[sIndex][qIndex] : ''
                                }))
                            })));
                        } else {
                            setQuestionsForNoSectionTest(prevQuestions => prevQuestions.map((question, qIndex) => ({
                                ...question,
                                student_pick: parsedAnswers[qIndex] ? parsedAnswers[qIndex] : ''
                            })));
                        }
                    }
                    // Populate FRQ answers from saved data
                    if (response.data.student_frq_answers) {
                        const restoredFrq = {};
                        const restoredImages = {};
                        response.data.student_frq_answers.forEach(ans => {
                            if (!restoredFrq[ans.question_id]) {
                                restoredFrq[ans.question_id] = {};
                            }
                            restoredFrq[ans.question_id][ans.part_label] = ans.response_text || '';
                            // Restore uploaded images
                            if (ans.response_images && ans.response_images.length > 0) {
                                if (!restoredImages[ans.question_id]) {
                                    restoredImages[ans.question_id] = {};
                                }
                                restoredImages[ans.question_id][ans.part_label] = ans.response_images;
                            }
                        });
                        setFrqAnswers(restoredFrq);
                        if (Object.keys(restoredImages).length > 0) {
                            setFrqUploadImages(restoredImages);
                        }
                    }
                }
            } catch (error) {
                if (error.response?.status === 401) {
                    handleLogout();
                } else if (error.response?.status === 404) {
                    setTestTakerInfo(null);
                    console.log("Test not found");
                }
            }
        };
    
        if (test && studentUuid) {
            if (userRole === "student" || isAdminProxy) {
                fetchStudentTestInfo(studentUuid);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [test, userRole, currentUser, courseId, testId, studentUuid]);
    
    
    const onDocumentLoadSuccess = ({ numPages }) => {
        setNumPages(numPages);
        setPdfLoading(false);
    };

    // Memoize pdf.js options to prevent re-triggering Document load on every render.
    // disableAutoFetch stops pdf.js from pre-downloading all pages after the first loads.
    const pdfOptions = React.useMemo(() => ({
        disableAutoFetch: true,
        disableStream: false,
    }), []);

    const documentRender = () => {
        // Use devicePixelRatio to render crisp PDFs on high-DPI displays (Windows scaling, Retina, etc.)
        // Minimum scale of 2 ensures crisp rendering even at DPR 1
        const pixelRatio = Math.max(window.devicePixelRatio || 1, 2);

        // Teachers see the watermarked PDF if available
        const pdfUrl = (userRole === "teacher" && test?.teacher_file_url) ? test.teacher_file_url : test?.file_url;

        return (
            <Document
                file={pdfUrl}
                options={pdfOptions}
                onLoadSuccess={onDocumentLoadSuccess}
                onContextMenu={(e) => e.preventDefault()}
                className="pdf-container"
                loading={
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                        <CircularProgress color="inherit" size={40} />
                    </Box>
                }
            >
                {Array.from(new Array(numPages), (el, index) => (
                    <Page
                        key={`page_${index + 1}`}
                        pageNumber={index + 1}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        scale={pixelRatio}
                        loading={
                            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                                <CircularProgress color="inherit" size={40} />
                            </Box>
                        }
                    />
                ))}
            </Document>
        );
    };

    const handleSectionAnswerChange = (sectionIndex, questionIndex, choice, isMultiSelect = false) => {
        const updatedSections = sections.map((section, sIndex) => {
            if (sIndex === sectionIndex) {
                const updatedQuestions = section.test_questions.map((question, qIndex) => {
                    if (qIndex === questionIndex) {
                        if (isMultiSelect) {
                            // Handle multi-select: toggle the choice in comma-separated string
                            const currentAnswers = question.student_pick ? question.student_pick.split(',') : [];
                            const choiceIndex = currentAnswers.indexOf(choice);
                            if (choiceIndex > -1) {
                                currentAnswers.splice(choiceIndex, 1);
                            } else {
                                currentAnswers.push(choice);
                            }
                            return { ...question, student_pick: currentAnswers.join(',') };
                        }
                        return { ...question, student_pick: choice };
                    }
                    return question;
                });
                return { ...section, test_questions: updatedQuestions };
            }
            return section;
        });
        setSections(updatedSections);
    };   
        
    const handleNoSectionAnswerChange = (questionIndex, choice, isMultiSelect = false) => {
        const updatedQuestions = questionsForNoSectionTest.map((question, qIndex) => {
            if (qIndex === questionIndex) {
                if (isMultiSelect) {
                    // Handle multi-select: toggle the choice in comma-separated string
                    const currentAnswers = question.student_pick ? question.student_pick.split(',') : [];
                    const choiceIndex = currentAnswers.indexOf(choice);
                    if (choiceIndex > -1) {
                        currentAnswers.splice(choiceIndex, 1);
                    } else {
                        currentAnswers.push(choice);
                    }
                    return { ...question, student_pick: currentAnswers.join(',') };
                }
                return { ...question, student_pick: choice };
            }
            return question;
        });
        setQuestionsForNoSectionTest(updatedQuestions);
    };

    const compareAnswers = (correctAnswer, studentPick, isMultiSelect) => {
        if (isMultiSelect) {
            // For multi-select, compare sorted comma-separated values
            const correctSet = (correctAnswer || '').split(',').filter(a => a).sort().join(',');
            const studentSet = (studentPick || '').split(',').filter(a => a).sort().join(',');
            return correctSet === studentSet;
        }
        return correctAnswer === studentPick;
    };

    const getScore = () => {
        let score = 0;

        if (test.has_section) {
            sections.forEach(section => {
                section.test_questions.forEach(question => {
                    // Skip FRQ and SA questions — they are graded separately
                    if (isFrqQuestion(question) || isSaQuestion(question)) return;
                    if (compareAnswers(question.answer, question.student_pick, question.is_multi_select)) {
                        score++;
                    }
                });
            });
        } else {
            questionsForNoSectionTest.forEach(question => {
                if (isFrqQuestion(question) || isSaQuestion(question)) return;
                if (compareAnswers(question.answer, question.student_pick, question.is_multi_select)) {
                    score++;
                }
            });
        }
        return score;
    };

    const formatAnswers = () => {
        let answers_text = '';
        if (test.has_section) {
            answers_text = sections.map(section => {
                return section.test_questions.map(question => {
                    // SA and FRQ answers are stored separately, use empty string in MCQ answer string
                    if (isFrqQuestion(question) || isSaQuestion(question)) return '';
                    return question.student_pick || '';
                }).join('|');
            }).join('@');
        } else {
            answers_text = questionsForNoSectionTest.map(question => {
                if (isFrqQuestion(question) || isSaQuestion(question)) return '';
                return question.student_pick || '';
            }).join('|');
        }
        return answers_text;
    };
    
    const handleSaveAnswers = () => {
        if (!canEdit) return;

        const formattedAnswers = formatAnswers();
        handlePauseTest(formattedAnswers);
    };

    const handleSubmitAnswers = () => {

        if (!canEdit) {
            setErrorAlertMessage("Cannot submit — select a student first");
            setErrorAlertOpen(true);
            return;
        }

        // if (!checkAllQuestionsAnswered()) {
        //     setErrorAlertMessage("Please answer all the questions before submitting.");
        //     setErrorAlertOpen(true);
        //     return;
        // }
        
        const formattedAnswers = formatAnswers();
        const score = getScore();

        handleStudentAnswerSubmit(formattedAnswers, score, false);
    };

    const handleSubmitAndGrade = () => {
        if (!canEdit) return;
        const formattedAnswers = formatAnswers();
        const score = getScore();
        handleStudentAnswerSubmit(formattedAnswers, score, true);
    };

    const handlePauseTest = async (formattedAnswers) => {
        // For debugging
        // console.log("formattedAnswers: ", formattedAnswers);
        
        try {
            const response = await Axios.put(`${process.env.REACT_APP_URL}/api/v1/courses/${courseId}/test-pause/${testId}/students/${studentUuid}/student-answers`, {
                student_answers: formattedAnswers,
                frq_answers: buildFrqAnswersPayload(),
            }, {
                headers: {
                    Authorization: getAuthHeader(),
                },
            });
    
            if (response.status === 200) {
                setSuccessAlertMessage("Your answers saved successfully!");
                setSuccessAlertOpen(true);
            } else {
                setErrorAlertMessage("Error saving progress!");
                setErrorAlertOpen(true);
            }
        } catch (error) {
            console.error(error);
            if (error.response?.status === 401) {
                handleLogout();
            } else {
                setErrorAlertMessage(error.response?.data?.message || "Something went wrong");
                setErrorAlertOpen(true);
            }
        }
    };
    

    const handleStudentAnswerSubmit = async (formattedAnswers, score, immediateGrade = false) => {

        await Axios.post(`${process.env.REACT_APP_URL}/api/v1/courses/${courseId}/test-taking/${testId}/students/${studentUuid}/student-answers`, {
            student_answers: formattedAnswers,
            score: score,
            frq_answers: buildFrqAnswersPayload(),
            immediate_grade: immediateGrade,
        }, {
            headers: {
                Authorization: getAuthHeader(),
            },
        }).then((response) => {
            setSuccessAlertMessage("Answer submitted successfully!");
            setSuccessAlertOpen(true);
            window.location.reload();
            // window.location.href = `/classrooms/${courseId}/tests`;
        }).catch((error) => {
            if (error.response?.status === 401) {
                handleLogout();
                return;
            }
            setErrorAlertMessage(error.response?.data?.message || "Something went wrong");
            setErrorAlertOpen(true);
        });
    }
    
    // Render image upload dropzone for a question/part
    const renderUploadDropzone = (questionId, partLabel, maxPoints) => {
        const uploadKey = `${questionId}_${partLabel}`;
        const images = (frqUploadImages[questionId] && frqUploadImages[questionId][partLabel]) || [];
        const isUploading = uploadingParts[uploadKey];
        const fileInputRef = React.createRef();

        return (
            <Box sx={{ mt: 1 }}>
                {maxPoints != null && (
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        {partLabel !== 'main' && partLabel !== 'response' ? `Part (${partLabel}) — ` : ''}{maxPoints} {maxPoints === 1 ? 'point' : 'points'}
                    </Typography>
                )}
                {/* Image previews */}
                {images.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                        {images.map((url, idx) => (
                            <Box key={idx} sx={{ position: 'relative', width: 120, height: 120, borderRadius: 1, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.2)' }}>
                                <img src={url} alt={`Upload ${idx + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                {canEdit && (
                                    <IconButton
                                        size="small"
                                        onClick={() => handleImageRemove(questionId, partLabel, idx)}
                                        sx={{ position: 'absolute', top: 2, right: 2, backgroundColor: 'rgba(0,0,0,0.6)', color: '#fff', '&:hover': { backgroundColor: 'rgba(200,0,0,0.8)' }, padding: '2px' }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>
                        ))}
                    </Box>
                )}
                {/* Upload area */}
                {canEdit && images.length < MAX_IMAGES_PER_PART && (
                    <Box
                        onClick={() => !isUploading && fileInputRef.current?.click()}
                        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                        onDrop={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!isUploading && e.dataTransfer.files?.length) {
                                handleImageUpload(questionId, partLabel, e.dataTransfer.files);
                            }
                        }}
                        sx={{
                            border: '2px dashed rgba(0,0,0,0.25)',
                            borderRadius: 2,
                            p: 3,
                            textAlign: 'center',
                            cursor: isUploading ? 'wait' : 'pointer',
                            backgroundColor: 'rgba(0,0,0,0.02)',
                            '&:hover': { borderColor: 'rgba(0,0,0,0.4)', backgroundColor: 'rgba(0,0,0,0.04)' },
                            transition: 'all 0.2s',
                        }}
                    >
                        {isUploading ? (
                            <CircularProgress size={24} />
                        ) : (
                            <>
                                <CloudUploadIcon sx={{ fontSize: 32, mb: 0.5, opacity: 0.6 }} />
                                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                    Click or drag to upload ({images.length}/{MAX_IMAGES_PER_PART})
                                </Typography>
                                <Typography variant="caption" sx={{ opacity: 0.5 }}>
                                    JPG, PNG, HEIC, WebP — max {MAX_FILE_SIZE_MB}MB per file
                                </Typography>
                            </>
                        )}
                        <input
                            ref={fileInputRef}
                            type="file"
                            hidden
                            multiple
                            accept="image/jpeg,image/png,image/heic,image/webp"
                            onChange={(e) => {
                                if (e.target.files?.length) {
                                    handleImageUpload(questionId, partLabel, e.target.files);
                                    e.target.value = '';
                                }
                            }}
                        />
                    </Box>
                )}
            </Box>
        );
    };

    // Render FRQ input fields for a question
    const renderFrqInput = (question) => {
        const qId = question.id;

        // Full upload question — entire question answered via images
        if (question.frq_type === 'upload') {
            return (
                <Box sx={{ mt: 1, mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        Upload your work — {question.max_points} {question.max_points === 1 ? 'point' : 'points'}
                    </Typography>
                    {renderUploadDropzone(qId, 'main', null)}
                </Box>
            );
        }

        if (question.frq_type === 'saq' && question.frq_parts && question.frq_parts.length > 0) {
            // SAQ: one input per part — text or upload depending on part_type
            return (
                <Box sx={{ mt: 1, mb: 1 }}>
                    {question.frq_parts.map((part) => (
                        <Box key={part.part_label} sx={{ mb: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                Part ({part.part_label}) — {part.max_points} {part.max_points === 1 ? 'point' : 'points'}
                            </Typography>
                            {part.part_type === 'upload' ? (
                                renderUploadDropzone(qId, part.part_label, null)
                            ) : (
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={3}
                                    maxRows={8}
                                    placeholder={`Your answer for part (${part.part_label})...`}
                                    value={(frqAnswers[qId] && frqAnswers[qId][part.part_label]) || ''}
                                    onChange={(e) => handleFrqAnswerChange(qId, part.part_label, e.target.value)}
                                    disabled={!canEdit}
                                    variant="outlined"
                                    sx={{ backgroundColor: 'rgba(255,255,255,0.05)' }}
                                />
                            )}
                        </Box>
                    ))}
                </Box>
            );
        } else {
            // DBQ / LEQ: single large TextField
            const partLabel = 'response';
            return (
                <Box sx={{ mt: 1, mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                        {question.frq_type === 'dbq' ? 'Document-Based Question' : question.frq_type === 'leq' ? 'Long Essay Question' : 'Free Response'}
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        minRows={6}
                        maxRows={20}
                        placeholder="Write your response here..."
                        value={(frqAnswers[qId] && frqAnswers[qId][partLabel]) || ''}
                        onChange={(e) => handleFrqAnswerChange(qId, partLabel, e.target.value)}
                        disabled={!canEdit}
                        variant="outlined"
                        sx={{ backgroundColor: 'rgba(255,255,255,0.05)' }}
                    />
                </Box>
            );
        }
    };

    // Render SA input field for a question (single-line text field, stored in frqAnswers)
    const renderSaInput = (question) => {
        const qId = question.id;
        const partLabel = 'response';
        return (
            <Box sx={{ mt: 1, mb: 1 }}>
                <TextField
                    fullWidth
                    placeholder="Type your answer..."
                    value={(frqAnswers[qId] && frqAnswers[qId][partLabel]) || ''}
                    onChange={(e) => handleFrqAnswerChange(qId, partLabel, e.target.value)}
                    disabled={!canEdit}
                    variant="outlined"
                    size="small"
                    sx={{ backgroundColor: 'rgba(255,255,255,0.05)' }}
                />
            </Box>
        );
    };

    // Collect all questions (flat) for choice group logic
    const getAllQuestions = () => {
        if (test?.has_section) {
            return sections.flatMap(s => s.test_questions);
        }
        return questionsForNoSectionTest;
    };

    // Check if a question is hidden by choice group selection
    const isQuestionHiddenByChoiceGroup = (question, allQuestions) => {
        if (!question.choice_group) return false;
        const selected = choiceGroupSelections[question.choice_group];
        // If nothing selected yet, show all (or default to first)
        if (selected === undefined) return false;
        return selected !== question.id;
    };

    // Render choice group selector if this is the first question in its group
    const renderChoiceGroupSelector = (question, allQuestions) => {
        if (!question.choice_group) return null;
        // Only render the selector for the first question in this group
        const groupQuestions = allQuestions.filter(q => q.choice_group === question.choice_group);
        if (groupQuestions[0]?.id !== question.id) return null;

        return (
            <Box sx={{ mb: 2, p: 2, border: '1px solid', borderColor: colors.blueAccent[400], borderRadius: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Choose one question to answer:
                </Typography>
                <RadioGroup
                    row
                    value={choiceGroupSelections[question.choice_group] ?? ''}
                    onChange={(e) => handleChoiceGroupChange(question.choice_group, parseInt(e.target.value, 10))}
                >
                    {groupQuestions.map(gq => (
                        <FormControlLabel
                            key={gq.id}
                            value={gq.id}
                            control={<Radio />}
                            label={`Question ${gq.question_number}`}
                            disabled={!canEdit}
                        />
                    ))}
                </RadioGroup>
            </Box>
        );
    };

    return (
        <Box m="20px" marginX={3} marginY={3}>
            <Header title={test?.course_tests[0].title} subtitle="A ONE Institute" />
            {loading ? 
                <Loading />
            : <>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        gap: 1.5,
                        flexWrap: 'wrap',
                        marginBottom: '10px',
                        mt: 5,
                    }}
                >                 
                    {/* Admin proxy: student picker */}
                    {isAdminProxy && (
                        <Autocomplete
                            size="small"
                            sx={{ width: 300, flexShrink: 0 }}
                            slotProps={{ paper: { sx: { color: '#222' } } }}
                            options={courseStudents}
                            getOptionLabel={(opt) => `${opt.first_name || ''} ${opt.last_name || ''} ${opt.korean_name ? `(${opt.korean_name})` : ''}`.trim()}
                            renderOption={(props, opt) => <li {...props} key={opt.uuid || opt.id}>{`${opt.first_name || ''} ${opt.last_name || ''} ${opt.korean_name ? `(${opt.korean_name})` : ''}`.trim()}</li>}
                            isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
                            value={courseStudents.find(s => s.uuid === proxyStudentUuid) || null}
                            onChange={(e, val) => {
                                setProxyStudentUuid(val?.uuid || '');
                                setTestTakerInfo(null);
                                setFrqAnswers({});
                                setFrqUploadImages({});
                                if (test?.has_section) {
                                    setSections(prev => prev.map(s => ({
                                        ...s,
                                        test_questions: s.test_questions.map(q => ({ ...q, student_pick: '' }))
                                    })));
                                } else {
                                    setQuestionsForNoSectionTest(prev => prev.map(q => ({ ...q, student_pick: '' })));
                                }
                            }}
                            renderInput={(params) => <TextField {...params} label="Submit on behalf of student" placeholder="Select student..." />}
                        />
                    )}
                    {/* Open PDF - only visible to admins to prevent downloads */}
                    {userRole === "admin" && (
                        <a href={test?.file_url} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
                            <Button variant="contained" size="small" startIcon={<OpenInNewIcon />}
                                sx={{ backgroundColor: colors.greenAccent[500], color: colors.primary[400], ':hover': { backgroundColor: colors.greenAccent[600] } }}>
                                Open PDF
                            </Button>
                        </a>
                    )}
                { userRole !== "student" &&
                    <Link to={`/classrooms/${courseId}/tests/${testId}/grading`} style={{ textDecoration: 'none' }}>
                        <Button variant="contained" size="small"
                            sx={{ backgroundColor: colors.blueAccent[600], color: 'white', ':hover': { backgroundColor: colors.blueAccent[700] } }}>
                            See Results
                        </Button>
                    </Link>
                }
                {/* 
                    1. Only students can save their progress 
                    2. If this is a student's first time taking the test, the button should be appeared
                    3. If the student has not completed (submitted) the test, the button should be appeared
                */}
                { canEdit && (isAdminProxy || !testTakerInfo || (testTakerInfo && testTakerInfo.is_completed === false)) &&
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                            sx={{
                                backgroundColor: colors.blueAccent[600],
                                color: 'white',
                                padding: '10px 30px',
                                borderRadius: '5px',
                                cursor: 'pointer',
                                boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.2)',
                                ':hover': {
                                    backgroundColor: "#00308F",
                                },
                                mr: 1,
                            }}
                            onClick={handleSaveAnswers}
                        >
                            Save
                        </Button>
                        <Button
                            sx={{ 
                                backgroundColor: "#DDA0DD", 
                                color: 'white', 
                                padding: '10px 20px', 
                                borderRadius: '5px', 
                                cursor: 'pointer',
                                boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.2)',
                                ':hover': {
                                    backgroundColor: "#800080",
                                },
                            }}
                            // onClick={handleSubmitAnswers}
                            onClick={handleModalOpen}
                        >
                            Submit
                        </Button>
                        {isAdminProxy && (
                            <Button
                                sx={{
                                    backgroundColor: "#FF6B35",
                                    color: 'white',
                                    padding: '10px 20px',
                                    borderRadius: '5px',
                                    cursor: 'pointer',
                                    boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.2)',
                                    ':hover': { backgroundColor: "#E55A2B" },
                                }}
                                onClick={handleSubmitAndGrade}
                            >
                                Submit & Grade
                            </Button>
                        )}
                        <StudentAnswerConfirmModal
                            open={modalOpen}
                            onClose={handleModalClose}
                            onSubmit={handleSubmitAnswers}
                            courseId={courseId}
                            testId={testId}
                            studentUuid={studentUuid}
                        />
                    </Box>
                }
                </Box>
                <Box
                    display="flex"
                    width="100%"
                    bgcolor={colors.primary[600]}
                >
                    <Box flex={dividerPosition} width="100%" sx={{ overflowY: 'scroll', height: '80vh', left: 0 }}>
                        <Box
                            sx={{
                                position: "sticky",
                                top: 0,
                            }}
                        >
                            {documentRender()}
                        </Box>
                    </Box>
                    <Divider 
                        orientation="vertical" 
                        flexItem
                        onMouseDown={handleMouseDown}
                        sx={{ cursor: 'col-resize', width: '5px', bgcolor: "grey" }}
                    />
                    <Box
                        flex={10 - dividerPosition}
                        ref={rightPaneRef}  
                        width="100%" 
                        p={5}
                        display='flex'
                        flexDirection='column'
                        gap={3}
                        sx={{
                            overflowY: 'auto',
                            overscrollBehavior: 'contain',
                            height: '80vh',
                        }}
                    > 
                    {/* Wtih Section */}
                        {test ? test.has_section ? (
                            <>
                                {sections.map((section, sectionIndex) => (
                                    <Box key={sectionIndex}>
                                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                            <Box>
                                                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
                                                    {section.section_name ? section.section_name : `Section ${section.section_number}`}
                                                </Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                                                    Number of Questions: {section.test_questions.length}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <ul>
                                            {section.test_questions.map((question, qIndex) => {
                                                const allQs = getAllQuestions();
                                                const hidden = isQuestionHiddenByChoiceGroup(question, allQs);
                                                return (
                                                <React.Fragment key={qIndex}>
                                                    {renderChoiceGroupSelector(question, allQs)}
                                                    {!hidden && (
                                                    <li>
                                                    <Typography variant="body1">
                                                        Q{question.question_number}
                                                        {!isFrqQuestion(question) && !isSaQuestion(question) && question.is_multi_select && (
                                                            <Typography component="span" variant="caption" sx={{ ml: 1, color: colors.blueAccent[400] }}>
                                                                (Select all that apply)
                                                            </Typography>
                                                        )}
                                                        {isFrqQuestion(question) && (
                                                            <Typography component="span" variant="caption" sx={{ ml: 1, color: colors.greenAccent[400] }}>
                                                                {question.frq_type === 'upload' ? '(Image Upload)' : '(Free Response)'}
                                                            </Typography>
                                                        )}
                                                        {isSaQuestion(question) && (
                                                            <Typography component="span" variant="caption" sx={{ ml: 1, color: colors.greenAccent[400] }}>
                                                                (Short Answer)
                                                            </Typography>
                                                        )}
                                                    </Typography>
                                                    {isFrqQuestion(question) ? (
                                                        renderFrqInput(question)
                                                    ) : isSaQuestion(question) ? (
                                                        renderSaInput(question)
                                                    ) : (
                                                    <ul>
                                                    {question.is_multi_select ? (
                                                        <FormGroup row>
                                                            {question.question_options.map((choice, cIndex) => (
                                                                <FormControlLabel
                                                                    key={cIndex}
                                                                    control={
                                                                        <Checkbox
                                                                            checked={(question.student_pick || '').split(',').includes(choice.option)}
                                                                            onChange={() => handleSectionAnswerChange(sectionIndex, qIndex, choice.option, true)}
                                                                            disabled={!canEdit}
                                                                        />
                                                                    }
                                                                    label={choice.option}
                                                                />
                                                            ))}
                                                        </FormGroup>
                                                    ) : (
                                                        <RadioGroup
                                                            row
                                                            value={question.student_pick || ''}
                                                            onChange={(e) => handleSectionAnswerChange(sectionIndex, qIndex, e.target.value)}
                                                        >
                                                            {question.question_options.map((choice, cIndex) => (
                                                                <FormControlLabel
                                                                    key={cIndex}
                                                                    value={choice.option}
                                                                    control={<Radio />}
                                                                    label={choice.option}
                                                                    disabled={!canEdit}
                                                                />
                                                            ))}
                                                        </RadioGroup>
                                                    )}
                                                    </ul>
                                                    )}
                                                    {userRole !== "student" && !isFrqQuestion(question) && !isSaQuestion(question) && (
                                                        <Box sx={{ mt: 2, mb: 2, color: "green" }}>
                                                            <Typography variant="body1">Correct Answer: {question.answer}</Typography>
                                                        </Box>
                                                    )}
                                                    </li>
                                                    )}
                                                </React.Fragment>
                                                );
                                            })}
                                        </ul>
                                    </Box>
                                ))}
                            </>
                        ) : (
                        // No Section
                        <>
                            <Box>
                                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                                            Number of Questions: {test.test_questions.length}
                                        </Typography>
                                    </Box>
                                <Box/>

                            </Box>
                                <ul>
                                    {questionsForNoSectionTest.map((question, qIndex) => {
                                        const allQs = getAllQuestions();
                                        const hidden = isQuestionHiddenByChoiceGroup(question, allQs);
                                        return (
                                        <React.Fragment key={qIndex}>
                                            {renderChoiceGroupSelector(question, allQs)}
                                            {!hidden && (
                                            <li>
                                            <Typography variant="body1">
                                                Q{question.question_number}
                                                {!isFrqQuestion(question) && !isSaQuestion(question) && question.is_multi_select && (
                                                    <Typography component="span" variant="caption" sx={{ ml: 1, color: colors.blueAccent[400] }}>
                                                        (Select all that apply)
                                                    </Typography>
                                                )}
                                                {isFrqQuestion(question) && (
                                                    <Typography component="span" variant="caption" sx={{ ml: 1, color: colors.greenAccent[400] }}>
                                                        (Free Response)
                                                    </Typography>
                                                )}
                                                {isSaQuestion(question) && (
                                                    <Typography component="span" variant="caption" sx={{ ml: 1, color: colors.greenAccent[400] }}>
                                                        (Short Answer)
                                                    </Typography>
                                                )}
                                            </Typography>
                                            {isFrqQuestion(question) ? (
                                                renderFrqInput(question)
                                            ) : isSaQuestion(question) ? (
                                                renderSaInput(question)
                                            ) : (
                                            <ul>
                                                {question.is_multi_select ? (
                                                    <FormGroup row>
                                                        {question.question_options.map((choice, cIndex) => (
                                                            <FormControlLabel
                                                                key={cIndex}
                                                                control={
                                                                    <Checkbox
                                                                        checked={(question.student_pick || '').split(',').includes(choice.option)}
                                                                        onChange={() => handleNoSectionAnswerChange(qIndex, choice.option, true)}
                                                                        disabled={!canEdit}
                                                                    />
                                                                }
                                                                label={choice.option}
                                                            />
                                                        ))}
                                                    </FormGroup>
                                                ) : (
                                                    <RadioGroup
                                                        row
                                                        value={question.student_pick || ''}
                                                        onChange={(e) => handleNoSectionAnswerChange(qIndex, e.target.value)}
                                                    >
                                                        {question.question_options.map((choice, cIndex) => (
                                                            <FormControlLabel
                                                                key={cIndex}
                                                                value={choice.option}
                                                                control={<Radio />}
                                                                label={choice.option}
                                                                disabled={!canEdit}
                                                            />
                                                        ))}
                                                    </RadioGroup>
                                                )}
                                            </ul>
                                            )}
                                            {userRole !== "student" && !isFrqQuestion(question) && !isSaQuestion(question) && (
                                                <Box sx={{ mt: 2, mb: 2, color: "green" }}>
                                                    <Typography variant="body1">Correct Answer: {question.answer}</Typography>
                                                </Box>
                                            )}
                                            </li>
                                            )}
                                        </React.Fragment>
                                        );
                                    })}
                                </ul>
                            </Box>
                        </>
                        ) : (
                            <Typography variant="h5" sx={{ 'fontWeight': 'bold' }}>
                                No Question found in this test
                            </Typography>
                        )}
                        </Box>
                    </Box>
                </>
            }
            <Alert
                successAlertOpen={successAlertOpen}
                setSuccessAlertOpen={setSuccessAlertOpen}
                errorAlertOpen={errorAlertOpen}
                setErrorAlertOpen={setErrorAlertOpen}
                successMsg={successAlertMessage}
                errorMsg={errorAlertMessage}
                vertical="bottom"
                horizontal="left"
            />
        </Box>
    )
}

export default TestSubmitPage;
